import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { practitionerService } from '../../services/practitionerService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import PracticeInformationStep from './components/PracticeInformationStep';
import ContactDetailsStep from './components/ContactDetailsStep';
import BrandingConfigurationStep from './components/BrandingConfigurationStep';
import AccountSetupStep from './components/AccountSetupStep';
import ReviewStep from './components/ReviewStep';

const PractitionerRegistration = () => {
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Practice Information
    practiceName: '',
    practitionerTitle: 'Dr',
    practitionerName: '',
    specialty: 'Endodontie',
    
    // Contact Details
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    referralEmail: '',
    
    // Branding
    slug: '',
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    logoUrl: '',
    
    // Account Setup
    accountEmail: '',
    accountPassword: '',
    accountFullName: '',
    
    // Settings
    status: 'active',
    notifyOnNewReferral: true,
    publicProfileVisible: true
  });

  const totalSteps = 5;

  const generateSlug = (name) => {
    return name
      ?.toLowerCase()
      ?.normalize('NFD')
      ?.replace(/[\u0300-\u036f]/g, '')
      ?.replace(/[^a-z0-9]+/g, '-')
      ?.replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from practice name
    if (field === 'practiceName') {
      setFormData(prev => ({ ...prev, slug: generateSlug(value) }));
    }
    
    // Auto-fill account email from practice email
    if (field === 'email' && !formData?.accountEmail) {
      setFormData(prev => ({ ...prev, accountEmail: value }));
    }
    
    // Auto-fill account full name from practitioner name
    if (field === 'practitionerName' && !formData?.accountFullName) {
      setFormData(prev => ({ 
        ...prev, 
        accountFullName: `${formData?.practitionerTitle} ${value}` 
      }));
    }
    
    if (error) setError('');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData?.practiceName || !formData?.practitionerName) {
          setError('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        break;
      case 2:
        if (!formData?.email || !formData?.phone) {
          setError('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex?.test(formData?.email)) {
          setError('Adresse email invalide');
          return false;
        }
        break;
      case 3:
        if (!formData?.slug) {
          setError('Le slug URL est obligatoire');
          return false;
        }
        // Slug validation
        const slugRegex = /^[a-z0-9-]+$/;
        if (!slugRegex?.test(formData?.slug)) {
          setError('Le slug ne peut contenir que des lettres minuscules, chiffres et tirets');
          return false;
        }
        break;
      case 4:
        if (!formData?.accountEmail || !formData?.accountPassword || !formData?.accountFullName) {
          setError('Veuillez remplir tous les champs du compte utilisateur');
          return false;
        }
        if (formData?.accountPassword?.length < 8) {
          setError('Le mot de passe doit contenir au moins 8 caractères');
          return false;
        }
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setError('');
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Create practitioner
      const practitionerData = {
        practiceName: formData?.practiceName,
        practitionerTitle: formData?.practitionerTitle,
        practitionerName: formData?.practitionerName,
        email: formData?.email,
        phone: formData?.phone,
        address: formData?.address,
        city: formData?.city,
        postalCode: formData?.postalCode,
        referralEmail: formData?.referralEmail,
        slug: formData?.slug,
        primaryColor: formData?.primaryColor,
        secondaryColor: formData?.secondaryColor,
        logoUrl: formData?.logoUrl,
        status: formData?.status
      };
      
      const createdPractitioner = await practitionerService?.create(practitionerData);
      
      // Create user account
      await practitionerService?.createUserAccount(
        formData?.accountEmail,
        formData?.accountPassword,
        formData?.accountFullName,
        createdPractitioner?.id
      );
      
      setSuccess(true);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/super-admin-dashboard');
      }, 2000);
    } catch (err) {
      console.error('Error creating practitioner:', err);
      setError(err?.message || 'Erreur lors de la création du praticien');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PracticeInformationStep
            formData={formData}
            onChange={handleInputChange}
          />
        );
      case 2:
        return (
          <ContactDetailsStep
            formData={formData}
            onChange={handleInputChange}
          />
        );
      case 3:
        return (
          <BrandingConfigurationStep
            formData={formData}
            onChange={handleInputChange}
          />
        );
      case 4:
        return (
          <AccountSetupStep
            formData={formData}
            onChange={handleInputChange}
          />
        );
      case 5:
        return (
          <ReviewStep formData={formData} />
        );
      default:
        return null;
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Praticien Créé avec Succès !</h2>
          <p className="text-gray-600 mb-4">
            Le compte praticien a été créé et les identifiants ont été envoyés par email.
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-2">Redirection en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Inscription Praticien - Super Admin</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
              <Icon name="UserPlus" size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Inscription Nouveau Praticien
            </h1>
            <p className="text-gray-600">
              Configuration complète du profil et du compte d'accès
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3, 4, 5]?.map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                    step < currentStep 
                      ? 'bg-green-600 text-white' 
                      : step === currentStep 
                      ? 'bg-blue-600 text-white' :'bg-gray-200 text-gray-600'
                  }`}>
                    {step < currentStep ? <Icon name="Check" size={20} /> : step}
                  </div>
                  {step < totalSteps && (
                    <div className={`flex-1 h-1 mx-2 ${
                      step < currentStep ? 'bg-green-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className={currentStep === 1 ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                Informations
              </span>
              <span className={currentStep === 2 ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                Contact
              </span>
              <span className={currentStep === 3 ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                Branding
              </span>
              <span className={currentStep === 4 ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                Compte
              </span>
              <span className={currentStep === 5 ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                Révision
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 mb-6">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Step Content */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8 mb-6">
            {renderStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/super-admin-dashboard')}
              className="flex items-center gap-2 px-6 py-3 text-gray-700 hover:bg-white rounded-lg font-medium transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Retour au Dashboard</span>
            </Button>
            
            <div className="flex items-center gap-3">
              {currentStep > 1 && (
                <Button
                  onClick={handlePrevious}
                  className="flex items-center gap-2 px-6 py-3 text-gray-700 hover:bg-white rounded-lg font-medium transition-colors"
                  disabled={loading}
                >
                  <Icon name="ChevronLeft" size={20} />
                  <span>Précédent</span>
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <span>Suivant</span>
                  <Icon name="ChevronRight" size={20} />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Création...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Check" size={20} />
                      <span>Créer le Praticien</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PractitionerRegistration;